/* eslint-disable react/no-unescaped-entities */
/* eslint-disable react-hooks/exhaustive-deps */
import { LightBulbIcon } from "@heroicons/react/20/solid";
import { Alert, Button, Flex, Group, Popover, Text } from "@mantine/core";
import React, {
  useLayoutEffect, useMemo, useRef, useState
} from "react";
import uuid from "react-uuid";
import BasicSelect from "../inputs/select/BasicSelect";

type Props = {
  list?: any[];
  onDone?: any;
  selectSort: any;
  selectSortDir: any;
  selectedColumns?: any[];
  children?: React.ReactNode;
  isOpen: any;
  setIsOpen: any;
};

const SortDropdown = ({
  list = [],
  isOpen = false,
  onDone,
  selectedColumns = [],
  setIsOpen,
  selectSortDir,
  children,
  selectSort,
}: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const [sort, setSort] = useState(selectSort);
  const [sortDir, setSortDir] = useState(selectSortDir);

  useLayoutEffect(() => {
    setSort(selectSort);
    setSortDir(selectSortDir);
  }, [selectSort]);

  const displayTraits: any = useMemo(
    () => list
      ?.filter((item: any) => selectedColumns.includes(item.name))
      .map((item) => ({
        id: uuid(),
        name: item?.display_name,
        type: item?.data_type,
        entityType: item?.attribute_type,
        value: item?.name,
      })),
    [list, selectedColumns]
  );
  const sortOrder: any = useMemo(() => {
    if (sort?.entityType === "metric_traits") {
      return [
        {
          name: "Number(1 → 9)",
          order: "asc",
          id: 1,
          type: "changeval",
        },
        {
          name: "Number(9 → 1)",
          order: "desc",
          id: 2,
          type: "changeval",
        },
        {
          name: "Change %(1 → 100)",
          order: "asc",
          id: 3,
          type: "growthval",
        },
        {
          name: "Change %(100 → 1)",
          order: "desc",
          id: 4,
          type: "growthval",
        },
      ];
    }
    if (sort.type === "string") {
      return [
        {
          name: "Text(A → Z)",
          order: "asc",
          id: 1,
          type: "property",
        },
        {
          name: "Text(Z → A)",
          order: "desc",
          id: 2,
          type: "property",
        },
      ];
    }
    if (sort.type === "string") {
      return [
        {
          name: "Text(A → Z)",
          order: "asc",
          id: 1,
          type: "property",
        },
        {
          name: "Text(Z → A)",
          order: "desc",
          id: 2,
          type: "property",
        },
      ];
    }
    if (sort.type === "datetime") {
      return [
        {
          name: "Date(New → Old)",
          order: "desc",
          id: 1,
          type: "property",
        },
        {
          name: "Date(Old → New)",
          order: "asc",
          id: 2,
          type: "property",
        },
      ];
    }
    if (sort.type === "int" || sort.type === "float") {
      return [
        {
          name: "Number(1 → 9)",
          order: "asc",
          id: 1,
          type: "property",
        },
        {
          name: "Number(9 → 1)",
          order: "desc",
          id: 2,
          type: "property",
        },
      ];
    }
    return [
      {
        name: "Ascending",
        order: "asc",
        id: 1,
        type: "property",
      },
      {
        name: "Descending",
        order: "desc",
        id: 2,
        type: "property",
      },
    ];
  }, [sort]);

  return (
    <Popover opened={isOpen} onChange={setIsOpen} position="bottom-end" shadow="lg" withinPortal>
      <Popover.Target>
        <div ref={ref} className="inline-block">
          {children}
        </div>
      </Popover.Target>
      <Popover.Dropdown>
          <Alert color="yellow" variant="light" radius="sm" icon={<LightBulbIcon className="w-4 h-4" />} className="mb-3">
            <Text size="xs" fw={500}>Not seeing what you're looking for? Try adding columns to the table.</Text>
          </Alert>
          <Flex gap={8} align="flex-start" justify="space-between">
            <BasicSelect
              containerClass="w-full"
              label="Sort By"
              selectedItem={displayTraits.find((item: any) => item.value === sort?.value)}
              list={displayTraits}
              setSelectedItem={setSort}
            />
            <BasicSelect
              containerClass="w-[80%]"
              label="Sort Order"
              list={sortOrder}
              selectedItem={sortOrder.find((item: any) => item.order === sortDir?.order && item.type === sortDir?.type) || sortOrder[0]}
              setSelectedItem={setSortDir}
            />
          </Flex>
          <Group justify="flex-end" mt="md" pt="sm" className="border-t gap-1">
            <Button variant="subtle" size="xs" color="violet" onClick={() => setIsOpen(false)}>Close</Button>
            <Button
              type="button"
              onClick={() => {
                onDone(sort, sortDir);
                setIsOpen(false);
              }}
              size="xs"
              className="px-4"
            >Done</Button>
          </Group>
      </Popover.Dropdown>
    </Popover>
  );
};

export default SortDropdown;
